import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import TooltipOverbookingTable from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-overbooking-table';
import TooltipStayRestrictionsTable from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-stay-restrictions-table';
import HotelPricingAssistantTooltipOtbMetricBlock from 'lumen/components/hotel/pricing-assistant/tooltip-otb-metric-block';
import type { Occupancy, Period, PredictedOccupancy } from 'lumen/models';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

import styles from './pricing-assistant-table-tooltip.module.css';

interface HotelPricingAssistantTableTooltipsTooltipDayTableHeaderCellSignature {
  Args: {
    date: Dayjs;
    period: Period;
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipDayTableHeaderCell extends Component<HotelPricingAssistantTableTooltipsTooltipDayTableHeaderCellSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];

  get formattedDate(): string {
    return this.args.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  get predictedOccupancyData(): PredictedOccupancy | undefined {
    return this.args.period.day.predictedOccupancy;
  }

  get referenceChangeText(): string {
    return `${this.intl.t('rates.changes.change_tooltip').toLowerCase()} ${this.intl
      .t('general.daysago', {
        count: this.args.period.referenceChangeDays,
      })
      .toLowerCase()}`;
  }

  get showOverbookingException(): boolean {
    return this.args.period.shouldShowOverbookingException;
  }

  get showOverbookingTooltip(): boolean {
    return this.access.hasPricingAssistantOverbookingRestrictionsFeature;
  }

  get showStayRestrictionsTooltip(): boolean {
    return (
      this.access.hasPricingAssistantMinLosRestrictionsFeature &&
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.MIN_LOS_STAY_RESTRICTION_TOOLTIP,
      )
    );
  }

  <template>
    <EmberTooltip
      @side="bottom"
      @tooltipClass="base-tooltip {{styles.container}}"
    >
      <div class="{{styles.header}} flex items-center justify-between p-400">
        <h4>{{this.formattedDate}}</h4>
        <p class="text-200--regular">{{this.referenceChangeText}}</p>
      </div>

      {{#if this.showOverbookingException}}
        <PrismBanner
          @bannerMessage={{t "pricing_assistant.push_error_code.generic_issue"}}
          @bannerStyle="critical"
          @bannerTitle={{t
            "pricing_assistant.exception.action_state.overbooking_title"
          }}
          @icon="circle-minus"
          @position="top"
        />
      {{/if}}
      <div class="flex justify-around px-400 py-300">
        <HotelPricingAssistantTooltipOtbMetricBlock
          @isCurrentOrFutureDay={{@period.isCurrentOrFutureDay}}
          @label={{t "pricing_assistant.my_OTB"}}
          @occupancyChange={{@period.occupancyChange}}
          @occupancyData={{this.occupancyData}}
          @showOccupancyChange={{@period.showOccupancyChange}}
        />
        {{#if this.access.hasOTBForecastView}}
          <HotelPricingAssistantTooltipOtbMetricBlock
            @isCurrentOrFutureDay={{@period.isCurrentOrFutureDay}}
            @label={{t "pricing_assistant.my_forecast"}}
            @occupancyChange={{@period.predictedOccupancyChange}}
            @occupancyData={{this.predictedOccupancyData}}
            @showOccupancyChange={{@period.showPredictedOccupancyChange}}
          />
        {{/if}}
      </div>

      {{#if this.showOverbookingTooltip}}
        <TooltipOverbookingTable @period={{@period}} />
      {{/if}}
      {{#if this.showStayRestrictionsTooltip}}
        <TooltipStayRestrictionsTable @period={{@period}} />
      {{/if}}
    </EmberTooltip>
  </template>
}
